import { Component, ElementRef, Input, ViewChild, EventEmitter, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { NotificationService } from 'app/services/notification.service';
import { UploadFileWithStatus } from 'app/models/file-upload/upload-file-with-status';
import { UtilityService } from 'app/services/utility.service';
import { ZipContentValidator } from 'app/shared/utils/zip-content-validator.util';

@Component({
	selector: 'app-drag-drop-file',
	templateUrl: './drag-drop-file.component.html',
	styleUrls: ['./drag-drop-file.component.css'],
	standalone: false,
})
export class DragDropFileComponent {
	@ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef<HTMLInputElement>;
	@Input() isUploadWithStatus = false;
	@Input() allowedExtensions: string[] = UtilityService.ALLOWED_FILE_EXTENSIONS;
	@Output() readonly fileRemoved = new EventEmitter<void>();
	@Input() maxFileSizeMB = 50;

	constructor(private notificationService: NotificationService, private readonly utilityService: UtilityService) {}

	get acceptAttribute(): string | null {
		return this.allowedExtensions?.length ? this.allowedExtensions.join(',') : null;
	}

	private isFileTypeAllowed(file: File): boolean {
		if (!this.allowedExtensions?.length) {
			return true;
		}
		const ext = file.name.includes('.') ? '.' + file.name.split('.').pop().toLowerCase() : '';
		if (!this.allowedExtensions.includes(ext)) {
			return false;
		}
		const mimeType = file.type.toLowerCase();
		if (!mimeType || mimeType === 'application/octet-stream') {
			return true;
		}
		const allowedMimes = UtilityService.EXTENSION_MIME_MAP[ext] ?? [];
		return allowedMimes.length === 0 || allowedMimes.includes(mimeType);
	}

	private async isZipWithRestrictedContent(item: File): Promise<boolean> {
		if (!(item?.name ?? '').toLowerCase().endsWith('.zip')) {
			return false;
		}
		try {
			const result = await ZipContentValidator.inspect(item);
			if (!result.allowed) {
				const restrictedNames = result.restrictedEntries.map((entry) => entry.name).join(', ');
				this.notificationService.showError(
					`The file "${item.name}" contains restricted file types that are not allowed: ${restrictedNames}. ` +
						'ZIP archives must not contain executable or script files.',
					'Invalid File Type'
				);
				return true;
			}
			return false;
		} catch {
			return false;
		}
	}

	files: any[] = [];
	retryFn: (file: File) => Observable<number>;
	/**
	 * on file drop handler
	 */
	async onFileDropped($event) {
		await this.prepareFilesList($event);
	}

	/**
	 * handle file from browsing
	 */
	async fileBrowseHandler(e: Event) {
		const input = e.target as HTMLInputElement;
		if (input.files) {
			const files = Array.from(input.files);
			await this.prepareFilesList(files);
		}
		input.value = '';
	}

	/**
	 * Delete file from files list
	 * @param index (File index)
	 */
	deleteFile(index: number) {
		if (index > -1) {
			const file = this.files[index];
			if (this.fileDropEl.nativeElement.files.length > 0) {
				this.removeFileFromFileList(this.fileDropEl.nativeElement.files, index);
			}
			this.files.splice(index, 1);
			if (file.status === 'failed') {
				this.fileRemoved.emit();
			}
		}
	}

	removeFileFromFileList(files, index) {
		const dt = new DataTransfer();

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (index !== i) dt.items.add(file); // here you exclude the file. thus removing it.
		}

		this.fileDropEl.nativeElement.files = dt.files; // Assign the updates list
	}

	/**
	 * Simulate the upload process
	 */
	uploadFilesSimulator(index: number, upload: Observable<HttpEvent<any>>): Promise<boolean> {
		this.files[index].progress = 0;
		return new Promise<boolean>((resolve) => {
			const file = this.files[index];
			upload.subscribe(
				(event) => {
					if (event.type === HttpEventType.Response) {
						this.files[index].progress = 100;
						resolve(true);
					} else if (event.type === HttpEventType.UploadProgress) {
						if (this.files[index].progress <= 90) {
							this.files[index].progress += 5;
						}
					}
				},
				() => {
					this.files[index].progress = 0;
					this.notificationService.showError(`Could not upload the file:${file.name}`, 'Error');
					resolve(false);
				},
				() => {
					resolve(false);
				}
			);
		});
	}

	/**
	 * Convert Files list to normal array list
	 * @param files (Files List)
	 */
	async prepareFilesList(files: Array<any>) {
		const MAX_FILE_SIZE = this.maxFileSizeMB * 1024 * 1024;
		const remainingSize = MAX_FILE_SIZE - this.files.reduce((acc, file) => acc + file.size, 0);

		for (const item of files) {
			if (!item || item.size <= 0) {
				this.notificationService.showError(
					'This file appears to be corrupted or empty. Please try again.',
					'Invalid File'
				);
				continue;
			}
			if (!this.isFileTypeAllowed(item)) {
				const allowed = this.allowedExtensions.map((e) => e.replace('.', '').toUpperCase()).join(', ');
				this.notificationService.showError(
					`The file "${item.name}" has an unsupported file type. Allowed types: ${allowed}.`,
					'Invalid File Type'
				);
				continue;
			}
			if (item.size > remainingSize) {
				// Show a more user-friendly error message
				this.notificationService.showError(
					`The file "${item.name}" is too large to add. Its size is ${this.formatBytes(
						item.size,
						2
					)}, but only ${this.formatBytes(remainingSize, 2)} is available for uploading.`,
					'File Size Error'
				);
				continue; // Skip adding this file
			}
			if (await this.isZipWithRestrictedContent(item)) {
				continue;
			}
			if (!this.isUploadWithStatus) {
				(item as any).progress = 0;
				this.files.push(item);
			} else {
				const fileWithPrefix = this.utilityService.setFileNameWithPrefix(item);
				const wrapped: UploadFileWithStatus = {
					file: fileWithPrefix,
					name: fileWithPrefix.name,
					size: fileWithPrefix.size,
					progress: 0,
					status: 'pending',
				};
				this.files.push(wrapped);
			}
		}
	}

	/**
	 * format bytes
	 * @param bytes (File size in bytes)
	 * @param decimals (Decimals point)
	 */
	formatBytes(bytes, decimals) {
		if (bytes === 0) {
			return '0 Bytes';
		}
		const k = 1024;
		const dm = decimals <= 0 ? 0 : decimals || 2;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
	}

	getTotalFilesSize(): number {
		return this.files.reduce((acc, file) => acc + file.size, 0);
	}

	/**
	 * Reset file input and clear the file list
	 */
	resetFileInput() {
		this.files = []; // Clear the files array
		if (this.fileDropEl && this.fileDropEl.nativeElement) {
			this.fileDropEl.nativeElement.value = ''; // Reset file input field
		}
	}
	async retryFile(index: number, retryFn: (file: File) => Observable<number>): Promise<boolean> {
		const file = this.files[index];
		if (file.status !== 'failed') return false;
		file.progress = 0;
		file.status = 'uploading';
		return new Promise<boolean>((resolve) => {
			try {
				const progress$ = retryFn(file.file);
				progress$.subscribe({
					next: (p) => (file.progress = p),
					complete: () => {
						file.progress = 100;
						file.status = 'done';
						resolve(true);
					},
					error: () => {
						file.progress = 0;
						file.status = 'failed';
						this.notificationService.showError(`Could not upload the file: ${file.name}`, 'Error');
						resolve(false);
					},
				});
			} catch {
				file.progress = 0;
				file.status = 'failed';
				resolve(false);
			}
		});
	}
	uploadFile(index: number, progress$: Observable<number>): Promise<boolean> {
		this.files[index].progress = 0;
		this.files[index].status = 'uploading';

		return new Promise<boolean>((resolve) => {
			progress$.subscribe({
				next: (p) => {
					this.files[index].progress = p;
				},
				complete: () => {
					this.files[index].progress = 100;
					this.files[index].status = 'done';
					resolve(true);
				},
				error: () => {
					this.files[index].progress = 0;
					this.files[index].status = 'failed';
					this.notificationService.showError(`Could not upload the file: ${this.files[index].name}`, 'Error');
					resolve(false);
				},
			});
		});
	}
}
